import type { FC } from 'react'

import type { WhiteLabel } from '@skytech/types'
import { InfoBox, StyledText } from '@skytech/ui'
import type { UseQueryResult } from '@tanstack/react-query'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import type { Policy } from '@/components/ecom-policy/types'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import type { MasterOrderAssetRequest } from '@/resources/ecom-policy-orders'

export const AgreedAssetsSummary: FC<{
  buyoutAssets: MasterOrderAssetRequest[]
  returnAssets: MasterOrderAssetRequest[]
  policiesQueriesWithBuyout: UseQueryResult<Policy>[]
}> = ({ buyoutAssets, policiesQueriesWithBuyout, returnAssets }) => {
  const { whiteLabel } = useAppContext()
  return (
    <InfoBox
      emphasis="info"
      className="!my-8 flex flex-col items-start justify-start text-sm sm:text-base"
    >
      <StyledText
        size="regular-plus"
        colorMode="black"
        fontWeight="bold"
        className="!mb-4 uppercase tracking-wider"
      >
        <Translation id="You agreed to:" />
      </StyledText>

      {/* BUYOUT assets */}
      {buyoutAssets.length > 0 && (
        <>
          <StyledText size="regular-plus" fontWeight="bold" className="uppercase tracking-wider">
            <Translation id="Buyout equipment" />
          </StyledText>
          <div
            className={twJoin(
              'h-0.5 w-full',
              bgStylesByWhiteLabel[whiteLabel] || bgStylesByWhiteLabel.skytech
            )}
          />

          <ul className="m-0 my-2 w-full p-0 pl-4">
            {buyoutAssets.map(({ assetId, policyId }, index) => {
              const policy = policiesQueriesWithBuyout.find(
                q => q.data && q.data.id === policyId
              )?.data
              if (!policy) return null
              const asset = policy.assets.find(a => a.id === assetId)
              const price = asset?.priceBuyout || 0

              return (
                <li key={index} className="flex py-0.5">
                  <div className="flex w-full justify-between">
                    {asset ? (
                      <span>{asset.description || <Translation id="Product name" />}</span>
                    ) : (
                      <span>
                        <Translation id="Asset #{assetId}" values={{ assetId }} />
                      </span>
                    )}
                    {Boolean(asset) && (
                      <FormattedNumberWithCurrency amount={price} currency={asset?.currency} />
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </>
      )}

      {/* RETURN assets */}
      {returnAssets.length > 0 && (
        <>
          <StyledText size="regular-plus" fontWeight="bold" className="uppercase tracking-wider">
            <Translation id="Return equipment" />
          </StyledText>
          <div
            className={twJoin(
              'h-0.5 w-full',
              bgStylesByWhiteLabel[whiteLabel] || bgStylesByWhiteLabel.skytech
            )}
          />

          <ul className="m-0 my-2 w-full p-0 pl-4">
            {returnAssets.map(({ assetId, policyId }, index) => {
              const policy = policiesQueriesWithBuyout.find(
                q => q.data && q.data.id === policyId
              )?.data
              if (!policy) return null
              const asset = policy.assets.find(a => a.id === assetId)
              return (
                <li key={index} className="flex py-0.5">
                  {asset ? (
                    <span>{asset.description || <Translation id="Product name" />}</span>
                  ) : (
                    <span>
                      <Translation id="Asset #{assetId}" values={{ assetId }} />
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </InfoBox>
  )
}

const bgStylesByWhiteLabel: Partial<Record<WhiteLabel, string>> = {
  skytech: 'bg-skytech-orange',
  control: 'bg-skytech-orange',
  tcm: 'bg-skytech-orange',
  ice: 'bg-[#ffc338]',
  arp: 'bg-arp',
  mobit: 'bg-mobit',
  evry: 'bg-tietoevry',
  tietoevry: 'bg-tietoevry',
  bechtle: 'bg-bechtle',
  csi: 'bg-csi-maroon',
  telia: 'bg-telia',
  elkjop: 'bg-elkjop-baseGreen',
  komplett: 'bg-komplett',
  threeStepIT: 'bg-threeStepIT',
  ikomm: 'bg-skytech-orange',
}
